import { Icon } from '@iconify/react';
import { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { Message, RootState, useAppDispatch, useUpdateAgentFeedbackMutation, updateAgentMessageState } from '@circuitry-ai/doc-data';
import { CtyButton, CtyShowToast, checkType, ErrorBoundary } from '@circuitry-ai/doc-ui';
import { CommentModal } from '../chatcommentmodal';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { chatOptionsDialogType } from '../feedbackoptions/expertoption';

export enum agentFeedbackOptions {
    COMMENT = 'Comment',
    LIKE = 'Like',
    DISLIKE = 'Dislike',
    RERUN = 'Rerun',
}

export type AgentFeedbackOptionsType = typeof agentFeedbackOptions[keyof typeof agentFeedbackOptions];

interface AgentChatOptionsProps {
    message: Partial<Message>,
    aiworker_id?: string,
    session_id?: string,
    msgIndex: number;
    onRerun?: (userInput: string, messageIndex: number) => void;
    previousUserInput?: string;
    showRerun?: boolean; // New prop to control rerun visibility
}

interface CommentProps {
    message: Partial<Message>;
    setShowCommentDialog: React.Dispatch<React.SetStateAction<chatOptionsDialogType | null>>;
}

const Comment: React.FC<CommentProps> = memo(({ message, setShowCommentDialog }) => {
    // Check if the message has comment history or a comment
    const hasComments = Boolean(
        (message?.comment_history && message.comment_history.length > 0) ||
        (message?.comment && message.comment.trim() !== '')
    );

    return (
        <CtyButton
            tooltipContent={agentFeedbackOptions.COMMENT}
            toolTipColor='warning'
            id={`comment-btn-${message?.messageId || 'agent'}`}
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={() => setShowCommentDialog(chatOptionsDialogType.COMMENT)}
        >
            <Icon
                icon={hasComments ? "fluent:comment-24-filled" : "fluent:comment-24-regular"}
                width={16}
                height={16}
                style={hasComments ? { color: "#0070f0" } : {}}
            />
        </CtyButton>
    );
});

interface LikeDislikeProps {
    feedbackType: 'like' | 'dislike',
    localStateFeedback: string,
    message: Partial<Message>,
    onFeedBack: (currentFeedbackState: string, prevFeedbackState: string) => void;
    aiworker_id?: string;
    session_id?: string;
}

const LikeDislike: React.FC<LikeDislikeProps> = memo(({ feedbackType, localStateFeedback, message, onFeedBack, aiworker_id, session_id }) => {
    const [showCommentDialog, setShowCommentDialog] = useState<boolean>(false);
    const isLike = feedbackType === 'like';
    const currentFeedback = localStateFeedback === feedbackType;
    const iconSize = isLike ? 19 : 16;

    const getIconProps = (isLike: boolean) => isLike
        ? { filledIcon: "gravity-ui:thumbs-up-fill", emptyIcon: "iconamoon:like-thin", color: 'green' }
        : { filledIcon: "gravity-ui:thumbs-down-fill", emptyIcon: "mynaui:dislike", color: '#ff0101' };

    const iconProps = getIconProps(isLike);

    const getButtonId = () => message?.messageId || 'agent';
    const getCurrentFeedbackState = () => {
        if (feedbackType === 'like' && currentFeedback) return 'remove_like';
        if (feedbackType === 'dislike' && currentFeedback) return 'remove_dislike';
        return feedbackType;
    };

    const handleClick = useCallback(() => {
        const currentFeedbackState = getCurrentFeedbackState();
        if (feedbackType === 'dislike' && !currentFeedback) {
            setShowCommentDialog(true);
        } else {
            onFeedBack(currentFeedbackState, localStateFeedback);
        }
    }, [feedbackType, currentFeedback, onFeedBack, localStateFeedback]);

    const isAgentHistoryPage = useLocation().pathname.includes('/history')

    return (
        <>
            <CtyButton
                tooltipContent={isLike ? agentFeedbackOptions.LIKE : agentFeedbackOptions.DISLIKE}
                toolTipColor='warning'
                id={`${feedbackType}-btn-${getButtonId()}`}
                isIconOnly
                radius="full"
                size="sm"
                variant="light"
                onPress={handleClick}
            >
                <Icon
                    className={currentFeedback ? "text-lg text-default-600" : ""}
                    style={currentFeedback ? { color: iconProps.color } : {}}
                    icon={currentFeedback ? iconProps.filledIcon : iconProps.emptyIcon}
                    height={currentFeedback ? undefined : iconSize}
                    width={currentFeedback ? undefined : iconSize}
                />
            </CtyButton>
            {showCommentDialog && (
                <CommentModal
                    data={message}
                    isCommentFromHistoryPage={isAgentHistoryPage}
                    getHistoryRefetch={() => { }}
                    onDialogClose={() => {
                        setShowCommentDialog(false);
                        onFeedBack('dislike', localStateFeedback);
                    }}
                    advisor_id={aiworker_id}
                    session_id={session_id}
                    isAgentFeedback={true}
                />
            )}
        </>
    );
});

interface RerunProps {
    onRerun?: (userInput: string, messageIndex: number) => void;
    previousUserInput?: string;
    message: Partial<Message>;
    isLoading?: boolean;
    messageIndex: number;
}

const Rerun: React.FC<RerunProps> = memo(({ onRerun, previousUserInput, message, isLoading = false, messageIndex }) => {
    const handleRerunClick = useCallback(() => {
        if (onRerun && previousUserInput) {
            onRerun(previousUserInput, messageIndex);
        } else {
            CtyShowToast(checkType.error, 'Previous input not found. Cannot rerun agent.');
        }
    }, [onRerun, previousUserInput, messageIndex]);

    return (
        <CtyButton
            tooltipContent={agentFeedbackOptions.RERUN}
            toolTipColor='warning'
            id={`rerun-btn-${message?.messageId || 'agent'}`}
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={handleRerunClick}
            isDisabled={isLoading || !previousUserInput}
        >
            {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            ) : (
                <Icon icon="mdi:refresh" width={16} height={16} />
            )}
        </CtyButton>
    );
});

const AgentChatOptions = ({
    message,
    aiworker_id,
    session_id,
    msgIndex,
    onRerun,
    previousUserInput,
    showRerun = true, // Default to true for backward compatibility
}: AgentChatOptionsProps) => {
    const dispatch = useAppDispatch();
    const isAgentHistoryPage = useLocation().pathname.includes('/history');
    const [updateAgentFeedback] = useUpdateAgentFeedbackMutation();

    // Auto-disable rerun on non-execute contexts if not explicitly set
    const shouldShowRerun = showRerun && !isAgentHistoryPage;
    const [showCommentDialog, setShowCommentDialog] = useState<chatOptionsDialogType | null>(null);
    const [isRerunning, setIsRerunning] = useState<boolean>(false);
    const { token } = useSelector((state: RootState) => state.auth);
    // Memoize feedback options list to prevent array recreation
    const feedbackOptionsList = useMemo(() => [
        agentFeedbackOptions.LIKE,
        agentFeedbackOptions.DISLIKE,
        agentFeedbackOptions.COMMENT,
        ...(shouldShowRerun ? [agentFeedbackOptions.RERUN] : [])
    ], [shouldShowRerun]);

    const { agentMessageState } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);
    const currentFeedback = agentMessageState?.[message?.messageId || '']?.feedback || '';
    const commentHistory = agentMessageState?.[message?.messageId || '']?.comments || [];

    // Add local state management like regular chat-options.tsx
    const [localStateFeedback, setLocalStateFeedback] = useState<string>(currentFeedback);
    const [isApiInProgress, setIsApiInProgress] = useState<boolean>(false);

    // Sync local state with Redux state changes only when not in API progress
    useEffect(() => {
        if (!isApiInProgress && currentFeedback !== localStateFeedback) {
            setLocalStateFeedback(currentFeedback);
        }
    }, [currentFeedback, localStateFeedback, isApiInProgress]);

    const onFeedBack = useCallback(async (currentFeedbackState: string, prevFeedbackState: string) => {
        // Update local state immediately for UI responsiveness
        setLocalStateFeedback(currentFeedbackState);
        setIsApiInProgress(true);

        const feedbackPayload = {
            feedback: currentFeedbackState,
            message_id: message?.messageId || '',
            ...(session_id ? { session_id } : {}),
            ...(aiworker_id ? { aiworker_id } : {}),
        };

        try {
            const response = await updateAgentFeedback(feedbackPayload).unwrap();
            // Update Redux state after successful API response
            dispatch(updateAgentMessageState({
                messageId: message?.messageId || '',
                updates: { feedback: currentFeedbackState }
            }));
            CtyShowToast(checkType.success, response?.message ?? 'feedback updated successfully');
        } catch (e) {
            CtyShowToast(checkType.error, (e as Error)?.message ?? 'Something went wrong!');
            // Revert local state on error
            setLocalStateFeedback(prevFeedbackState);
        } finally {
            setIsApiInProgress(false);
        }
    }, [dispatch, updateAgentFeedback, message?.messageId, session_id, aiworker_id]);

    const handleRerun = useCallback(async (userInput: string, messageIndex: number) => {
        try {
            setIsRerunning(true);
            if (onRerun) {
                await onRerun(userInput, messageIndex);
                CtyShowToast(checkType.success, 'Agent rerun initiated successfully');
            }
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to rerun agent. Please try again.');
        } finally {
            setIsRerunning(false);
        }
    }, [onRerun]);

    // Create message object with comment history for components that need it
    const messageWithCommentHistory = {
        ...message,
        comment_history: commentHistory,
        comment: commentHistory.length > 0 ? commentHistory[commentHistory.length - 1]?.comment || '' : ''
    };

    const renderFeedback = useCallback((option: AgentFeedbackOptionsType) => {
        switch (option) {
            case agentFeedbackOptions.COMMENT:
                return <Comment
                    message={messageWithCommentHistory}
                    setShowCommentDialog={setShowCommentDialog}
                />;
            case agentFeedbackOptions.LIKE:
                return <LikeDislike
                    feedbackType="like"
                    message={message}
                    localStateFeedback={localStateFeedback}
                    onFeedBack={onFeedBack}
                    aiworker_id={aiworker_id}
                    session_id={session_id} />;
            case agentFeedbackOptions.DISLIKE:
                return <LikeDislike
                    feedbackType="dislike"
                    message={message}
                    localStateFeedback={localStateFeedback}
                    onFeedBack={onFeedBack}
                    aiworker_id={aiworker_id}
                    session_id={session_id}
                />;
            case agentFeedbackOptions.RERUN:
                return <Rerun
                    onRerun={handleRerun}
                    previousUserInput={previousUserInput}
                    message={message}
                    isLoading={isRerunning}
                    messageIndex={msgIndex}
                />;
        }
    }, [message, messageWithCommentHistory, localStateFeedback, onFeedBack, aiworker_id, session_id, handleRerun, previousUserInput, isRerunning, msgIndex]);

    return (
        <div className="w-fit flex rounded-full bg-content1 chat-feedback-container border px-1 mt-1">
            {feedbackOptionsList.map((option: AgentFeedbackOptionsType) => {
                return <div key={option}>
                    {renderFeedback(option)}
                </div>;
            })}
            {showCommentDialog === chatOptionsDialogType.COMMENT && (
                <CommentModal
                    data={messageWithCommentHistory}
                    isCommentFromHistoryPage={isAgentHistoryPage}
                    getHistoryRefetch={() => { }}
                    onDialogClose={() => setShowCommentDialog(null)}
                    advisor_id={aiworker_id}
                    session_id={session_id}
                    isAgentFeedback={true}
                />
            )}
        </div>
    );
};

// Wrap with ErrorBoundary
const AgentChatOptionsWithErrorBoundary = ErrorBoundary(AgentChatOptions);

export { AgentChatOptions, AgentChatOptionsWithErrorBoundary };
